import { CartAddEvent } from '@theme/events';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const FEEDBACK_MS = 2000;

/** @param {number} value */
const pad = (value) => String(value).padStart(2, '0');

/**
 * Mr. Brown "Ofertas Relámpago": countdown, carousel arrows, quantity steppers and add to cart
 * wired to Horizon's cart drawer through CartAddEvent.
 */
class MrbFlashOffers extends HTMLElement {
  #timer = 0;
  #resizeObserver = new ResizeObserver(() => this.#layout());

  connectedCallback() {
    this.#startCountdown();

    const track = this.track;
    if (track) {
      track.addEventListener('scroll', this.#updateArrows, { passive: true });
      this.#resizeObserver.observe(track);
    }

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#resizeObserver.disconnect();
    this.track?.removeEventListener('scroll', this.#updateArrows);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.querySelector('[data-carousel-track]');
  }

  /** @returns {number | null} */
  #getEndTime() {
    const now = new Date();

    if (this.dataset.countdownMode === 'date') {
      const end = Date.parse(this.dataset.countdownEnd ?? '');
      return Number.isNaN(end) ? null : end;
    }

    const resetHour = Number(this.dataset.countdownResetHour) || 0;
    const end = new Date(now);
    end.setHours(resetHour, 0, 0, 0);
    if (end.getTime() <= now.getTime()) end.setTime(end.getTime() + DAY);
    return end.getTime();
  }

  #startCountdown() {
    const countdown = this.querySelector('[data-countdown]');
    if (!(countdown instanceof HTMLElement)) return;

    const hours = countdown.querySelector('[data-hours]');
    const minutes = countdown.querySelector('[data-minutes]');
    const seconds = countdown.querySelector('[data-seconds]');
    let end = this.#getEndTime();
    if (end === null) return;

    const tick = () => {
      let distance = /** @type {number} */ (end) - Date.now();

      if (distance <= 0) {
        if (this.dataset.countdownMode !== 'date') {
          end = this.#getEndTime();
          distance = /** @type {number} */ (end) - Date.now();
        } else {
          distance = 0;
          window.clearInterval(this.#timer);
          if (this.dataset.hideExpired === 'true' && !window.Shopify?.designMode) {
            this.hidden = true;
          }
        }
      }

      if (hours) hours.textContent = pad(Math.floor(distance / HOUR));
      if (minutes) minutes.textContent = pad(Math.floor((distance % HOUR) / 60000));
      if (seconds) seconds.textContent = pad(Math.floor((distance % 60000) / 1000));
    };

    tick();
    countdown.hidden = false;
    this.#timer = window.setInterval(tick, 1000);
  }

  #layout() {
    const media = this.querySelector('.mrb-flash-card__media');
    const carousel = this.querySelector('.mrb-flash-offers__carousel');
    if (media instanceof HTMLElement && carousel instanceof HTMLElement) {
      const center = media.offsetTop + media.offsetHeight * 0.85;
      carousel.style.setProperty('--mrb-fo-arrow-top', `${center}px`);
    }
    this.#updateArrows();
  }

  #updateArrows = () => {
    const track = this.track;
    const carousel = this.querySelector('.mrb-flash-offers__carousel');
    if (!track || !(carousel instanceof HTMLElement)) return;

    carousel.dataset.scrollable = String(track.scrollWidth - track.clientWidth > 1);
  };

  /** @param {number} direction */
  #scrollBy(direction) {
    const track = this.track;
    const slide = track?.querySelector('.mrb-flash-offers__slide');
    if (!track || !(slide instanceof HTMLElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (direction > 0 && track.scrollLeft >= maxScroll - 1) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (direction < 0 && track.scrollLeft <= 1) {
      track.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = slide.offsetWidth + gap;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    track.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-carousel-prev]')) return this.#scrollBy(-1);
    if (target.closest('[data-carousel-next]')) return this.#scrollBy(1);

    const step = target.closest('[data-qty-step]');
    if (step instanceof HTMLButtonElement) {
      const input = step.closest('[data-flash-card]')?.querySelector('[data-qty-input]');
      if (input instanceof HTMLInputElement) {
        this.#setQuantity(input, Number(input.value) + Number(step.dataset.qtyStep));
      }
      return;
    }

    const add = target.closest('[data-add-to-cart]');
    if (add instanceof HTMLButtonElement) this.#addToCart(add);
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-qty-input]')) {
      this.#setQuantity(input, Number(input.value));
    }
  };

  /**
   * @param {HTMLInputElement} input
   * @param {number} value
   */
  #setQuantity(input, value) {
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Number.isFinite(value) ? Math.min(Math.max(Math.round(value), min), max) : min;
    input.value = String(next);
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const card = button.closest('[data-flash-card]');
    const input = card?.querySelector('[data-qty-input]');
    const quantity = input instanceof HTMLInputElement ? Number(input.value) || 1 : 1;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const label = button.textContent?.trim() ?? '';
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.textAdding || label;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Add to cart failed: ${response.status}`);
      }

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'mrb-flash-offers',
          itemCount: quantity,
          productId: button.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      if (this.dataset.openDrawer === 'true') {
        const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
          document.querySelector('cart-drawer-component')
        );
        if (drawer?.open && !drawer.hasAttribute('auto-open')) drawer.open();
      }

      button.textContent = this.dataset.textAdded || label;
    } catch (error) {
      console.error(error);
      button.textContent = this.dataset.textError || label;
    } finally {
      window.setTimeout(() => {
        button.textContent = label;
        button.removeAttribute('aria-busy');
      }, FEEDBACK_MS);
    }
  }
}

if (!customElements.get('mrb-flash-offers')) {
  customElements.define('mrb-flash-offers', MrbFlashOffers);
}
